import type { NavItem } from "@avalara/skylab-sdk";

const toPathname = (url: string): string => {
    try {
        return new URL(url, window.location.origin).pathname;
    } catch {
        return url;
    }
};

// Strips a trailing slash (except on the bare root "/") so a route and a nav item's url compare
// equal regardless of which side happens to carry one — e.g. the shell may register the home nav
// item as "https://host/exemptions/" (trailing slash) while the route on that page is "/exemptions"
// (none), and without normalizing both sides first, `startsWith(itemPath + "/")` only catches the
// mismatch when the *route* has the extra slash, not when the *item* does.
const normalizePath = (path: string): string =>
    path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;

const isPathMatch = (currentPath: string, itemPath: string): boolean => {
    const normalizedCurrent = normalizePath(currentPath);
    const normalizedItem = normalizePath(itemPath);
    return (
        normalizedCurrent === normalizedItem || normalizedCurrent.startsWith(`${normalizedItem}/`)
    );
};

/**
 * Resolves the `dataRef` of the most specific nav item (a submenu entry, falling back to its
 * top-level parent when no submenu matches) whose `url` matches the given route.
 *
 * Only the deepest match needs to be returned: Skylab's aui-sidenav/aui-supernav cascade the
 * "active" class up the DOM tree to ancestor <li> elements once a descendant's active-ref
 * matches (see `_AuiSidenav_toggleActiveClass`), so setting `active` to a submenu item's dataRef
 * already highlights both that item and its parent tab.
 *
 * Shared between the AvaApp (OWD) shell-nav highlighting and standalone mode, since both need to
 * resolve "current route -> nav dataRef" against the same nav item tree shape.
 */
export const resolveActiveNavRef = (
    navItems: NavItem[] | undefined,
    currentPath: string
): string | undefined => {
    let bestMatch: { dataRef: string; pathLength: number } | undefined;

    const visit = (items: NavItem[] | undefined): void => {
        items?.forEach(item => {
            if (item.url) {
                const itemPath = toPathname(item.url);
                if (
                    isPathMatch(currentPath, itemPath) &&
                    (!bestMatch || itemPath.length > bestMatch.pathLength)
                ) {
                    bestMatch = { dataRef: item.dataRef, pathLength: itemPath.length };
                }
            }
            visit(item.menuItems);
        });
    };

    visit(navItems);
    return bestMatch?.dataRef;
};

export default resolveActiveNavRef;
